import moment from "moment";
import "moment/locale/nl"; // Import Dutch locale for moment.js
import { removeOffer, setModals, setOffers, } from "../redux/slices/appconfigSlice";
import {
    setDepartureFrom, setDesireCountry, setGPTTo,
    setPaginatedRoomFlights,
    setPaginatedRoomTypes,
    setSelectedHotel,
    setSelectedHotelDetail, setSelectedHotelToShare,
} from "../redux/slices/searchFilterSlice";
import { AddToFavoriteList, RemoveFromFavoriteList, } from "../services/FavoriteHotelService";
import { resetBookingState, setBothFlight, setHotelBooking, setHotelRoom, } from "../redux/slices/bookingSlice";
import { GetAirportsCodesService } from "../services/AirportsService";
import { SearchCountries, ServicePostPunchOutToExternal } from "../services/AppService";
import { captureDetailUrl, getDetailUrl, isDetailSessionLive } from "../utils/detailUrl";

export const _EMAIL_REG_EXP = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;

export const openExternalLink = async (url, existingTab = null) => {
    if (!url || typeof window === "undefined") {
        return;
    }

    if (existingTab) {
        existingTab.location.href = url;
        return;
    }

    window.open(url, "_blank");
};

export const postPunchOutAndOpenExternal = async ({
    url,
    transactionId,
    guid,
    dispatch,
    setActivityIndicator,
    existingTab = null,
}) => {
    if (!url) {
        if (existingTab) {
            existingTab.close();
        }
        return;
    }

    const payload = {
        punchOutUrl: url,
        transaction_id: transactionId,
        GUID: guid,
    };

    dispatch(setActivityIndicator(true));
    try {
        await ServicePostPunchOutToExternal(payload);
        dispatch(setActivityIndicator(false));
        await openExternalLink(url, existingTab);
    } catch {
        dispatch(setActivityIndicator(false));
        if (existingTab) {
            existingTab.close();
        }
    }
};

/**
 * Replaces the value of a specific query parameter in the URL and returns the updated URL.
 * @param {string} key - The query parameter key to replace.
 * @param {string} value - The new value for the query parameter.
 * @returns {string} - The updated URL.
 */
export const replaceQueryParam = (key, value) => {
    // Parse the canonical detail URL and its query parameters. Reading the
    // canonical URL instead of window.location keeps working after the address
    // bar is swapped for the share URL.
    const url = new URL(getDetailUrl());
    const params = new URLSearchParams(url.search);

    // Replace the value of the specified query parameter
    params.set(key, value);

    // Keep the canonical URL current, so the value can be read back below.
    url.search = params.toString();
    captureDetailUrl(url.toString());

    // Construct the updated URL
    return params.toString();
}

export const getQueryParamValue = (key) => {
    const url = new URL(getDetailUrl());
    const params = new URLSearchParams(url.search);

    // Get the value of the specified query parameter
    return params.get(key);
};

export const getCurrencyBadge = (currency) => {
    let badge = "$";
    switch (currency) {
        case "EUR":
            badge = "€";
            break;

        case "USD":
            badge = "$";
            break;

        case "GBP":
            badge = "£";
            break;

        default:
            break;
    }
    return badge;
};

export const getCurrencyName = (currency) => {
    let name = "Euro";
    switch (currency) {
        case "EUR":
            name = "Euro";
            break;

        case "USD":
            name = "Dollar";
            break;

        case "GBP":
            name = "Pound";
            break;

        default:
            break;
    }
    return name;
};

/**
 * Get random package from array.
 * @param {array of pacagkes} items
 * @returns package Item as object
 */
export const getRandomPackageFromArray = (items) => {
    return items[Math.floor(Math.random() * items.length)];
};

export const paginateData = (array, page_size, page_number) => {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
};

export const getPager = (totalItems, currentPage = 1, pageSize = 10, maxPages = 10) => {
    // calculate total pages
    let totalPages = Math.ceil(totalItems / pageSize);

    // ensure current page isn't out of range
    if (currentPage < 1) {
        currentPage = 1;
    } else if (totalPages > 0 && currentPage > totalPages) {
        // Guarded on totalPages: with no items this branch used to drag
        // currentPage down to 0, which is not a real page.
        currentPage = totalPages;
    }

    let startPage, endPage;
    if (totalPages <= maxPages) {
        // less than or equal to maxPages total pages so show all
        startPage = 1;
        endPage = totalPages;
    } else {
        // more than maxPages total pages so calculate start and end pages
        let halfPages = Math.floor(maxPages / 2);
        if (currentPage <= halfPages + 1) {
            startPage = 1;
            endPage = maxPages;
        } else if (currentPage + halfPages >= totalPages) {
            startPage = totalPages - maxPages + 1;
            endPage = totalPages;
        } else {
            startPage = currentPage - halfPages;
            endPage = currentPage + halfPages;
        }
    }

    // calculate start and end item indexes
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    let pages = Array.from(Array(endPage + 1 - startPage).keys()).map(
        (i) => startPage + i
    );

    // return object with all pager properties required by the view
    return {
        totalItems: totalItems,
        currentPage: currentPage,
        pageSize: pageSize,
        totalPages: totalPages,
        startPage: startPage,
        endPage: endPage,
        startIndex: startIndex,
        endIndex: endIndex,
        pages: pages,
    };
};

export const getPagerMiddlePage = (totalItems, pageSize = 10) => {
    const totalPages = Math.ceil(totalItems / pageSize);

    if (totalPages <= 0) {
        return 0;
    }

    return Math.ceil(totalPages / 2);
};

export const getListingItemFilters = (offer, T, flight) => {
    let filters = [];
    if (offer && offer?.roomTypes?.length) {
        const { checkInDate, checkOutDate } = getCheckInOutDatesFromFlight(flight);
        // Created start and end date
        // const checkInDate = checkInDate;
        // const checkOutDate = checkOutDate;
        const firstChip = {
            id: 1,
            title: `${moment.parseZone(checkInDate).format("MMM DD")}-${moment.parseZone(
                checkOutDate
            ).format("DD")}`,
        };
        filters.push(firstChip);

        // Creating room and person count from URL. The href is absent on
        // partially-hydrated offers, and `new URL(undefined)` throws.
        let objUrl = null;
        try {
            objUrl = new URL(offer?.roomTypes?.[0]?.links?.webDetails?.href);
        } catch {
            objUrl = null;
        }
        if (objUrl) {
            const searchparams = new URLSearchParams(objUrl?.search);
            let total = 0;
            const roomsCount = searchparams.get("NumRoom");
            const adultsCount = searchparams.get('NumAdult')?.split('|').reduce((previous, current) => previous + Number(current.split(':')[1]), 0);
            const childCount = searchparams.get('NumChild')?.split('|').reduce((previous, current) => previous + Number(current.split(':')[1]), 0);

            if (adultsCount) {
                total = Number(total) + Number(adultsCount);
            }
            if (childCount) {
                total = Number(total) + Number(childCount);
            }
            const secondChip = {
                id: 2,
                title: `${total} ${T('Home:Home_List_View_Pers')} / ${roomsCount} ${T('Home:Home_List_View_Rm')}`
            };
            filters.push(secondChip);
        }

        // Creating days of staying
        let nights = nightDifferenceCountForFlightsDate(checkInDate, checkOutDate);
        nights = Number(nights);
        const thirdChip = {
            id: 3,
            title: `${nights} ${nights > 1 ? T("Common:Nights") : T("Common:Night")}`,
        };
        filters.push(thirdChip);
    } else {
        // Created start and end date
        const checkInDate = offer?.departureDate;
        const checkOutDate = offer?.returnDate;
        const firstChip = {
            id: 1,
            title: `${moment.parseZone(checkInDate).format("MMM DD")}-${moment.parseZone(
                checkOutDate
            ).format("DD")}`,
        };
        filters.push(firstChip);

        // Creating room and person count from URL
        const roomsCount = 1;
        const adultsCount = offer?.adults;
        const childCount = offer?.children;
        let total = 0;
        if (adultsCount) {
            total = Number(total) + Number(adultsCount);
        }
        if (childCount) {
            total = Number(total) + Number(childCount);
        }
        const secondChip = {
            id: 2,
            title: `${total} ${T('Home:Home_List_View_Pers')} / ${roomsCount} ${T('Home:Home_List_View_Rm')}`
        };
        filters.push(secondChip);

        // Creating days of staying
        const nights = nightDifferenceCountForFlightsDate(checkInDate, checkOutDate);
        // nights = Number(nights);
        const thirdChip = {
            id: 3,
            title: `${nights} ${nights > 1 ? T("Common:Nights") : T("Common:Night")}`,
        };
        filters.push(thirdChip);
    }

    return filters;
};

export const nightDifferenceCountForFlightsDate = (start, end) => {
    const checkInDate = moment.parseZone(start).format('YYYY-MM-DD');
    const checkOutDate = moment.parseZone(end).format('YYYY-MM-DD');

    return moment(checkOutDate).diff(moment(checkInDate), 'days');
};

export const navigateToDetailPage = (
    offer,
    dispatch,
    loginConfig,
    searchfilters,
    navigate,
    page,
    replace = true,
    isFavorite = false,
    chatOffer = false,
    branchData = null,
    flightNumbersString = null,
    // mapOffer = false
) => {
    const pathUrl = window.location.pathname + window.location.search;
    localStorage.setItem('roomFetched', 'false');
    localStorage.setItem('flightFetched', 'false');
    // The session check covers a detail page whose address bar has been swapped
    // for the share URL, so it never overwrites prevUrl with itself.
    if (!isDetailSessionLive() && !pathUrl.includes('/hotel/')) {
        localStorage.setItem('prevUrl', pathUrl.toString());
    }

    const { adults, childs, infants } = getTravelersData(searchfilters);

    const flightData = offer?.objHotel?.newPackagedOffer?.flightOffer; // Selected flight from redux

    // departure ticket data
    const departureSegmentId = flightData?.segmentIds?.[0];
    const departureFlightSegment = flightData?.segments?.filter(
        (f) => f.segmentId === departureSegmentId
    )?.[0];
    const departureFlight = departureFlightSegment?.legs?.[0];
    // End of data

    // Arrival ticket data
    const arrivalSegmentId =
        flightData?.segmentIds[flightData?.segmentIds?.length - 1];
    const arrivalFlightSegment = flightData?.segments?.filter(
        (f) => f.segmentId === arrivalSegmentId
    )?.[0];
    const returnFlight = arrivalFlightSegment?.legs?.[0];
    // End of data


    const params = {
        hotelId: offer?.id,
        upSell: false,
        Adults: adults,
        Children: childs,
        Infants: infants,
        Locale: loginConfig.locale.code,
        fav: isFavorite,
        chatOffer,
        flightNumbersString,
        // mapOffer,
        roomKey: offer?.roomTypes?.[0]?.roomKey,
        GUID: offer?.guidForBooking,
        offerId: offer?.objHotel?.newPackagedOffer?.flightOffer?.offerId || null,
        NumberOfStops: searchfilters?.numberOfStops,
        Departure: departureFlight?.departureAirport?.code,
        Destination: returnFlight?.departureAirport?.code,
        DepartureDate: moment.parseZone(departureFlight?.departureDateTime).format(
            "YYYY-MM-DD"
        ),
        ReturnDate: moment.parseZone(returnFlight?.departureDateTime).format("YYYY-MM-DD"),
        apiDetailsUri: offer?.roomTypes?.[0]?.links?.apiDetails?.href,
    };

    if (searchfilters?.flightCabinClass) {
        params["flightCabinClass"] = searchfilters?.flightCabinClass;
    }

    dispatch(resetBookingState());
    dispatch(setPaginatedRoomTypes([]));
    dispatch(setPaginatedRoomFlights([]));
    // The following dispatch method will used to add earlier data in the short link.
    dispatch(setSelectedHotelToShare(offer));

    let queryString = "";

    Object.keys(params)?.forEach((element, index) => {
        queryString += `${element}=${params[element]}`;
        if (Object.keys(params)?.length - 1 !== index) {
            queryString += "&";
        }
    });

    // If there is branch data in the app config,
    //  it means user has come from a branch link.
    //  In that case we will not pass the query params to the detail page because the detail page will get the data from the app config and passing query params may create conflict.
    //  Also we want to avoid having two different links for the same hotel detail page (one with query params and one without) for better user experience and SEO.
    //  So if there is branch data, we will navigate to the detail page without query params and the detail page will get the data from app config based on the hotel id.
    //  This will ensure that even if user shares the link, it will work without any issue because it will not rely on query params to show the hotel details.
    if (branchData) {
        navigate(-1);
        return;
    }

    // Capture the readable URL before navigating. This is the only place it is
    // ever built, so it is the earliest and most reliable point to preserve it.
    captureDetailUrl(`${window.location.origin}/${page}/query?${queryString}`);

    if (replace) {
        navigate(`/${page}/query?${queryString}`, { replace: true });
    } else {
        navigate(`/${page}/query?${queryString}`);
    }
};

export const handleFavoriteHotelItem = (
    loginconfig,
    dispatch,
    searchfilters,
    offer,
    appconfig,
    index = null,
    handler = null,
    flightData
) => {
    if (loginconfig?.loginUser?.guid && loginconfig?.isLogin) {
        // console.log("OFFER", offer);
        if (!offer?.favoriteGuid) {
            const infants = searchfilters.travelers.reduce(
                (n, e) => (e.infants ? n + e.infants : n),
                0
            );

            const objUrl = new URL(offer?.roomTypes?.[0]?.links?.webDetails?.href);
            const searchparams = new URLSearchParams(objUrl.search);
            const adultsCount = searchparams
                .get("NumAdult")
                ?.split("|")
                .reduce(
                    (previous, current) => previous + Number(current.split(":")[1]),
                    0
                );
            const childCount = searchparams
                .get("NumChild")
                ?.split("|")
                .reduce(
                    (previous, current) => previous + Number(current.split(":")[1]),
                    0
                );

            // const flightData = offer?.objHotel?.newPackagedOffer?.flightOffer;
            const departureSegmentId = flightData?.segmentIds?.[0];
            const departureFlightSegment = flightData?.segments?.filter(
                (f) => f.segmentId === departureSegmentId
            )?.[0];
            const departureAirport =
                departureFlightSegment?.legs?.[0]?.departureAirport?.code;

            const { checkInDate, checkOutDate } =
                getCheckInOutDatesFromFlight(flightData);

            const postObject = {
                guid: loginconfig?.loginUser?.guid,
                hotelId: offer?.id,
                country: offer?.location?.address?.country,
                hotel: offer?.name,
                location: offer?.location?.address?.city,
                starrating: offer?.starRating?.split(".")[0],
                Departure: departureAirport,
                departuredate: checkInDate,
                returndate: checkOutDate,
                price: offer?.price?.totalPrice,
                adults: adultsCount || 1,
                children: childCount || 0,
                infants: infants || 0,
                detailslink: offer?.roomTypes?.[0]?.links?.apiDetails?.href,
                hotelimg:
                    offer?.thumbnailUrl ||
                    "http://www.geniustravel.com/background-image-banner.jpg",
            };

            // console.log({postObject});

            AddToFavoriteList(postObject)
                .then((response) => {
                    // console.log({index});
                    if (index === null) {
                        dispatch(
                            setSelectedHotel({
                                ...offer,
                                favoriteGuid: response?.data?.favoriteGuid,
                            })
                        );
                        dispatch(
                            setHotelBooking({
                                ...offer,
                                favoriteGuid: response?.data?.favoriteGuid,
                            })
                        );
                    } else {
                        let _offers = [...appconfig.offers];
                        _offers[index] = {
                            ...offer,
                            favoriteGuid: response?.data?.favoriteGuid,
                        };
                        dispatch(setOffers(_offers));
                    }
                })
                .catch((error) => {
                    console.error(error);
                    dispatch(removeOffer(offer?.id));
                });
        } else {
            // const _index = appconfig.favPkgs?.findIndex((i) => i?.id === offer?.id);
            RemoveFromFavoriteList({
                guid: loginconfig?.loginUser?.guid,
                favoriteguid: offer?.favoriteGuid,
            }).then(() => {
                if (handler) {
                    handler(index);
                }
                if (index === null) {
                    dispatch(setSelectedHotel({ ...offer, favoriteGuid: null }));
                    dispatch(setHotelBooking({ ...offer, favoriteGuid: null }));
                } else {
                    let _offers = [...appconfig.offers];
                    let _index = [...appconfig.offers].findIndex((f, i) => i === index);
                    if (_index !== -1) {
                        _offers[_index] = {
                            ...offer,
                        };
                        _offers[_index]["favoriteGuid"] = null;
                        dispatch(setOffers(_offers));
                    }
                }
            });
        }
    } else {
        dispatch(setModals({ login: true, register: false, confirmation: false }));
    }
};

export const getFlightObjectFromGPTOffer = (appConfig, GUIDinURL, hotelId) => {
    const _offers = appConfig.chatGPTOffers;
    const hotel = _offers?.find(f => f.guidForBooking === GUIDinURL && f.id === hotelId);
    return hotel?.objHotel?.newPackagedOffer?.flightOffer || null
}

export const getFlightsParams = (
    room,
    searchfilters,
    loginconfig,
    booking,
    appConfig,
    hotelId,
    guid,
    isFav = false,
    fromChat = false,
    flightNumbersStringFromUrl = null
) => {
    const { adults, childs, infants } = getTravelersData(searchfilters);

    let flightData;

    if (isFav) {
        if (booking.flight) {
            flightData = booking.flight;
        } else {
            flightData = searchfilters.roomFlights[0];
        }
    } else if (fromChat) {
        flightData = getFlightObjectFromGPTOffer(appConfig, guid, hotelId)
    } else {
        flightData = getFlightOfferFromAppConfigOffers(appConfig, hotelId);
    }

    // departure ticket data
    const departureSegmentId = flightData?.segmentIds?.[0];
    const departureFlightSegment = flightData?.segments?.filter(
        (f) => f.segmentId === departureSegmentId
    )?.[0];
    const departureFlight = departureFlightSegment?.legs?.[0];
    // End of data

    // Arrival ticket data
    const arrivalSegmentId =
        flightData?.segmentIds[flightData?.segmentIds?.length - 1];
    const arrivalFlightSegment = flightData?.segments?.filter(
        (f) => f.segmentId === arrivalSegmentId
    )?.[0];
    const returnFlight = arrivalFlightSegment?.legs?.[0];
    // End of data

    // Flight numbers of the selected package offer, in itinerary order, pipe-separated.
    // Lets the backend return the originally selected flight as the first result.
    const flightNumbersString = flightData?.segmentIds
        ?.map((segmentId) =>
            flightData?.segments?.find((segment) => segment.segmentId === segmentId)
        )
        ?.flatMap((segment) => segment?.legs ?? [])
        ?.map((leg) => leg?.flightNumber)
        ?.filter(Boolean)
        ?.join("|"); // returns string like "AA123|AA456|AA789"

    // This indicates that this package is being open from shared link.
    const hasFlightNumbersFromUrl = Boolean(flightNumbersStringFromUrl) && flightNumbersStringFromUrl !== "null";

    const params = {
        Departure: departureFlight?.departureAirport?.code,
        Destination: returnFlight?.departureAirport?.code,
        DepartureDate: moment.parseZone(departureFlight?.departureDateTime).format(
            "YYYY-MM-DD"
        ),
        ReturnDate: moment.parseZone(returnFlight?.departureDateTime).format("YYYY-MM-DD"),
        Adults: adults,
        Children: childs,
        Infants: infants,
        Locale: loginconfig?.locale?.code,
        currency: loginconfig?.currency?.code,
        NumberOfStops: searchfilters?.numberOfStops,
        RoomKey: room?.roomKey,
        GUID: guid,
        FlightOfferId: booking?.flight?.offerId || null,
        flightNumbersString: hasFlightNumbersFromUrl ? flightNumbersStringFromUrl : flightNumbersString,
        flightOriginator: hasFlightNumbersFromUrl ? "LINK" : "",
    };

    if (searchfilters?.flightCabinClass) {
        params["flightCabinClass"] = searchfilters?.flightCabinClass;
    }

    return params;
}

export const getTravelersData = (searchFilters) => {
    // Callers pass the whole searchfilters slice, which can be mid-reset.
    const travelers = Array.isArray(searchFilters?.travelers)
        ? searchFilters.travelers
        : [];
    const adults = travelers.reduce(
        (n, e) => (e.adult ? n + e.adult : n),
        0
    );
    const infants = travelers.reduce(
        (n, e) => (e.infants ? n + e.infants : n),
        0
    );
    const childs = travelers.reduce(
        (n, e) => (e.child ? n + e.child : n),
        0
    );

    return { adults, infants, childs, total: adults + infants + childs };
};

export const getFlightsDataFromFlightOffer = (flightOffer) => {
    // departure ticket data
    const departureSegmentId = flightOffer?.segmentIds?.[0];
    const departureFlightSegment = flightOffer?.segments?.filter(
        (f) => f.segmentId === departureSegmentId
    )?.[0];
    const departureFlight = departureFlightSegment?.legs?.[0];
    // End of data

    // Arrival ticket data
    const arrivalSegmentId =
        flightOffer?.segmentIds?.[flightOffer?.segmentIds?.length - 1];
    const arrivalFlightSegment = flightOffer?.segments?.filter(
        (f) => f.segmentId === arrivalSegmentId
    )?.[0];
    const returnFlight = arrivalFlightSegment?.legs?.[0];

    return { departureFlight, returnFlight };
};

export const getHotelQuickFiltersParams = (
    searchFilters,
    loginConfig,
    appConfig,
    guid,
    page = 1
) => {
    let quickSearchParams = {
        GUID: guid,
        locale: `${loginConfig.locale.code}`,
        Page: page,
        FilterPrePost: {
            minPrice: searchFilters.defaultPrice.low,
            maxPrice: searchFilters.defaultPrice.high,
        },
        // SortOrder: searchFilters.sortingOrder,
    };

    let passengerAgeRoomItemList = [];
    let roomCounter = 1;
    searchFilters.travelers.forEach((element) => {
        for (let i = 0; i < element.infants; i++) {
            passengerAgeRoomItemList.push({
                PassengerCategory: "Infant",
                Room: roomCounter,
            });
        }
        for (let i = 0; i < element.adult; i++) {
            passengerAgeRoomItemList.push({
                PassengerCategory: "Adult",
                Room: roomCounter,
            });
        }
        for (let i = 0; i < element.child; i++) {
            passengerAgeRoomItemList.push({
                PassengerCategory: "Child",
                Room: roomCounter,
            });
        }
        roomCounter++;
    });

    if (passengerAgeRoomItemList.length > 0) {
        quickSearchParams["PassengerAgeRoomItemList"] = passengerAgeRoomItemList;
    }

    let flightSegmentList = [];
    let inbound_params = {};
    let outbound_params = {};

    const { departureFlight, returnFlight } = getFlightsDataFromFlightOffer(
        appConfig?.offers?.[0]?.objHotel?.newPackagedOffer?.flightOffer
    );

    inbound_params.Departure = {
        CodeList: [departureFlight?.departureAirport?.code],
        Date: moment(searchFilters.travelDate.startDate).format("YYYY-MM-DD"),
    };

    inbound_params.Arrival = {
        CodeList: [returnFlight?.departureAirport?.code],
    };

    outbound_params.Departure = {
        CodeList: [returnFlight?.departureAirport?.code],
        Date: moment(searchFilters.travelDate.endDate).format("YYYY-MM-DD"),
    };

    outbound_params.Arrival = {
        IataCodeList: [departureFlight?.departureAirport?.code],
    };

    flightSegmentList.push(inbound_params);
    flightSegmentList.push(outbound_params);

    if (flightSegmentList.length > 0) {
        quickSearchParams["FlightSegmentList"] = flightSegmentList;
    }

    // console.log({quickSearchParams});

    return quickSearchParams;
};

export const getFlightOfferFromAppConfigOffers = (appConfig, hotelId) => {
    const hotel = appConfig.offers?.find((f) => f.id === hotelId);

    if (hotel) {
        return {
            price: hotel?.objHotel?.price,
            links: hotel.roomTypes[0].links,
            surcharges: 0,
            ...hotel?.objHotel?.newPackagedOffer?.flightOffer,
        };
    }
    return null;
};

export const calculateRoomsUpSell = (room, roomTypes, dispatch) => {
    return new Promise((resolve) => {
        const selectedRoomRateDifference = Number(room?.rateDifference);
        const updatedRoomsArray = roomTypes?.map((_room) => {
            return {
                ..._room,
                rateDifference: (
                    Number(_room.rateDifference) - selectedRoomRateDifference
                ).toFixed(2),
            };
        });
        dispatch(setSelectedHotelDetail(updatedRoomsArray));
        dispatch(setHotelRoom(room));
        resolve();
    });
};

export const getSearchOnlyListingParams = ({
    guid = "",
    page = 1,
    searchfilters,
    loginconfig,
    initialSearch = false,
    bigSearch = false,
    SearchType,
    searchCoordinates = ''
}) => {
    let PassengerAgeRoomItemList = [];
    let roomCounter = 1;
    let FlightSegmentList = [];
    let inbound_params = {};
    let outbound_params = {};

    let searchParams = {
        GUID: guid,
        Locale: `${loginconfig?.locale?.code}`,
        Page: page,
        FilterPrePost: {
            minPrice: searchfilters?.defaultPrice?.low,
            maxPrice: searchfilters?.defaultPrice?.high,
            // minStarRating: searchfilters.starRating.min,
            // maxStarRating: searchfilters.starRating.max,
            // flightCabinClass: searchfilters?.flightCabinClass,
        },
        SortOrder: 'Price',
        currency: loginconfig?.currency?.code,
        SearchType,
    }

    if (!initialSearch) {
        searchfilters?.travelers?.forEach((element) => {
            for (let i = 0; i < element.infants; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Infant",
                    Room: roomCounter,
                });
            }
            for (let i = 0; i < element.adult; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Adult",
                    Room: roomCounter,
                });
            }
            for (let i = 0; i < element.child; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Child",
                    Room: roomCounter,
                });
            }
            roomCounter++;
        });

        // console.log(searchfilters?.departureFrom, searchfilters?.selectedDesireCountry);

        if (searchfilters?.departureFrom?.length && (searchCoordinates || searchfilters?.selectedDesireCountry)) {
            inbound_params.Departure = {
                CodeList: searchfilters.departureFrom?.map((m) => m?.code),
                Date: moment(new Date(searchfilters.travelDate?.startDate))?.format("YYYY-MM-DD"),
            };

            if (
                searchfilters.travelTime &&
                searchfilters.travelTime?.departureTime &&
                searchfilters.travelTime?.departureTime !== "Anytime"
            ) {
                inbound_params.Departure["StartTime"] =
                    searchfilters.travelTime?.departureTime?.split("-")[0];
                inbound_params.Departure["EndTime"] =
                    searchfilters.travelTime?.departureTime?.split("-")[1];
            }

            inbound_params.Arrival = {
                CodeList: searchCoordinates
                    ? [searchCoordinates]
                    : [
                        searchfilters?.selectedDesireCountry?.id === "00001" &&
                            searchfilters?.selectedDesireCountry?.airportCode === "001"
                            ? `[ALL][000001][Anywhere]`
                            : `[${searchfilters.selectedDesireCountry?.airportCode}][${searchfilters.selectedDesireCountry?.id}][${searchfilters.selectedDesireCountry?.keyword}]`,
                    ],
            };

            inbound_params["NumberOfStops"] =
                searchfilters.appFilters.find((f) => f.key === "steps")?.id ||
                searchfilters.numberOfStops;

            outbound_params.Departure = {
                CodeList: searchCoordinates
                    ? [searchCoordinates]
                    : [
                        searchfilters?.selectedDesireCountry?.id === "00001" &&
                            searchfilters?.selectedDesireCountry?.airportCode === "001"
                            ? `[ALL][000001][Anywhere]`
                            : `[${searchfilters.selectedDesireCountry?.airportCode}][${searchfilters.selectedDesireCountry?.id}][${searchfilters.selectedDesireCountry?.keyword}]`,
                    ],
                Date: moment(new Date(searchfilters.travelDate.endDate)).format("YYYY-MM-DD"),
            };

            if (
                searchfilters.travelTime &&
                searchfilters.travelTime?.arrivalTime &&
                searchfilters.travelTime?.arrivalTime !== "Anytime"
            ) {
                outbound_params.Departure["StartTime"] =
                    searchfilters.travelTime?.arrivalTime?.split("-")[0];
                outbound_params.Departure["EndTime"] =
                    searchfilters.travelTime?.arrivalTime?.split("-")[1];
            }

            outbound_params.Arrival = {
                IataCodeList: searchfilters.departureFrom?.map((m) => m.code),
            };

            outbound_params["NumberOfStops"] =
                searchfilters.appFilters.find((f) => f.key === "steps")?.id ||
                searchfilters.numberOfStops;

            FlightSegmentList.push(inbound_params);
            FlightSegmentList.push(outbound_params);
        }

        if (FlightSegmentList.length > 0) {
            searchParams["FlightSegmentList"] = FlightSegmentList;
        }

        if (PassengerAgeRoomItemList.length > 0) {
            searchParams["PassengerAgeRoomItemList"] = PassengerAgeRoomItemList;
        }
    } else if (!location.pathname.includes("/search/")) {
        searchParams.FilterPrePost.minStarRating = searchfilters.starRating.min;
        searchParams.FilterPrePost.maxStarRating = searchfilters.starRating.max;
    }

    if (bigSearch) {
        searchParams["FilterPrePost"]["minStarRating"] =
            searchfilters.appFilters.find((f) => f.key === "guest_rating")?.id ||
            searchfilters.starRating.min;
        searchParams["FilterPrePost"]["maxStarRating"] =
            searchfilters.starRating.max;
        searchParams["FilterPrePost"]["flightCabinClass"] =
            searchfilters.appFilters.find((f) => f.key === "airline_class")?.value ||
            searchfilters?.flightCabinClass;

        const hotelAmenitiesArray = searchfilters.appFilters.filter(
            (f) => f.key === "popular_filters"
        );
        if (hotelAmenitiesArray?.length) {
            let _PackageFilter = [];
            hotelAmenitiesArray.forEach((f) => {
                _PackageFilter.push({ name: f?.value });
            });
            searchParams["FilterPrePost"]["PackageFilter"] = _PackageFilter;
        }
    }

    return searchParams;
};

export const getThreeFilterToShow = (room, T, flight) => {
    let filters = [];

    try {
        if (room !== null) {
            // Created start and end date
            // const checkInDate = room?.ratePlans?.[0]?.stayDates?.checkInDate;
            // const checkOutDate = room?.ratePlans?.[0]?.stayDates?.checkOutDate;
            const { checkInDate, checkOutDate } = getCheckInOutDatesFromFlight(flight);
            const firstChip = {
                id: 1,
                title: `${moment.parseZone(checkInDate).format("MMM DD")}-${moment.parseZone(
                    checkOutDate
                ).format("DD")}`,
            };
            filters.push(firstChip);

            // Creating room and person count from URL
            const objUrl = new URL(room?.links?.webDetails?.href);
            if (objUrl) {
                const searchparams = new URLSearchParams(objUrl?.search);
                const roomsCount = searchparams.get("NumRoom");
                const adultsCount = searchparams
                    .get("NumAdult")
                    ?.split("|")
                    .reduce(
                        (previous, current) => previous + Number(current.split(":")[1]),
                        0
                    );
                const childCount = searchparams
                    .get("NumChild")
                    ?.split("|")
                    .reduce(
                        (previous, current) => previous + Number(current.split(":")[1]),
                        0
                    );
                let total = 0;
                if (adultsCount) {
                    total = Number(total) + Number(adultsCount);
                }

                if (childCount) {
                    total = Number(total) + Number(childCount);
                }

                const secondChip = {
                    id: 2,
                    title: `${total} ${T('Home:Home_List_View_Pers')} / ${roomsCount} ${T('Home:Home_List_View_Rm')}`,
                };
                filters.push(secondChip);
            }

            // Creating days of staying
            let nights = nightDifferenceCountForFlightsDate(checkInDate, checkOutDate);
            nights = Number(nights);
            const thirdChip = {
                id: 3,
                title: `${nights} ${nights > 1 ? T("Common:Nights") : T("Common:Night")
                    }`,
            };
            filters.push(thirdChip);
        }
    } catch (e) {
        console.error(e);
    }

    return filters;
};

export const updateFlightPrice = (
    totalTravelers,
    priceToAdd,
    previousSelectedFlight,
    dispatch,
    newFlight = null,
    isItChooseRoom = false,
) => {
    let obj = { ...previousSelectedFlight?.price };
    obj.packageTotalPrice = {
        value: (
            Number(obj?.packageTotalPrice?.value) +
            priceToAdd * totalTravelers
        ).toFixed(2),
        currency: obj?.packageTotalPrice?.currency,
    };
    obj.standAloneTotalPrice = {
        value: (
            Number(obj?.standAloneTotalPrice?.value) +
            priceToAdd * totalTravelers
        ).toFixed(2),
        currency: obj?.standAloneTotalPrice?.currency,
    };
    obj.averageCostPerPerson = {
        value: (Number(obj?.averageCostPerPerson?.value) + priceToAdd).toFixed(2),
        currency: obj?.averageCostPerPerson?.currency,
    };

    if (isItChooseRoom) {
        return { ...previousSelectedFlight, price: obj };
    }

    if (newFlight === null) {
        dispatch(
            setBothFlight({
                newFlight: { ...previousSelectedFlight, price: obj },
                oldFlight: previousSelectedFlight,
            }),
        );
    } else if (newFlight) {
        dispatch(
            setBothFlight({
                newFlight: { ...newFlight, price: obj },
                oldFlight: previousSelectedFlight,
            }),
        );
    }
};

export const getTravelerCountFromRoomUrl = url => {
    let total = 0;
    if (url) {
        // Relative or malformed hrefs used to throw out of this helper.
        let objUrl = null;
        try {
            objUrl = new URL(url);
        } catch {
            return total;
        }
        if (objUrl) {
            const searchParams = new URLSearchParams(objUrl.search);
            const adultsCount = searchParams
                .get('NumAdult')
                ?.split('|')
                .reduce(
                    (previous, current) => previous + Number(current.split(':')[1]),
                    0,
                );
            const childCount = searchParams
                .get('NumChild')
                ?.split('|')
                .reduce(
                    (previous, current) => previous + Number(current.split(':')[1]),
                    0,
                );
            if (adultsCount) {
                total = Number(total) + Number(adultsCount);
            }
            if (childCount) {
                total = Number(total) + Number(childCount);
            }
        }
    }
    return total;
};

export const moveNumberToEnd = str => {
    const regex = /(\d+)-?(\s|$)/; // Match numbers followed by "-" or whitespace
    const match = str.match(regex);

    if (match) {
        const number = match[1];
        const prefix = str.replace(regex, ''); // Remove the number and "-" or whitespace
        const finalString = number + ' ' + prefix.trim(); // Add a space after the number and trim any extra spaces
        // trim() again: slicing a trailing "-" leaves the space before it.
        return (finalString.endsWith('-') ? finalString.slice(0, -1) : finalString).trim();
    } else {
        return str; // Return the string unchanged if no match is found
    }
};

export const getCheckInOutDatesFromFlight = flight => {
    // Index off the optional chain, not through it: `flight?.segmentIds?.[0]`
    // guards the object but still indexes an undefined list.
    const departureSegmentId = flight?.segmentIds?.[0];
    const departureFlightSegment = flight?.segments?.filter(
        f => f.segmentId === departureSegmentId,
    )?.[0];
    const departureStart = departureFlightSegment?.legs?.[0];


    const arrivalSegmentId = flight?.segmentIds?.[flight?.segmentIds?.length - 1];
    const arrivalFlightSegment = flight?.segments?.filter(
        f => f.segmentId === arrivalSegmentId,
    )?.[0];
    const arrivalStart = arrivalFlightSegment?.legs?.[0];


    const checkInDate = departureStart?.departureDateTime;
    const checkOutDate = arrivalStart?.departureDateTime;

    return {
        checkInDate,
        checkOutDate,
    };
};


export const getAirportFullObject = async ({ text, locale, dispatch }) => {
    try {
        const response = await GetAirportsCodesService(text, locale, 'name');
        const airport = response?.airports?.find(f => f.code === text);
        if (airport) {
            dispatch(
                setDepartureFrom([
                    {
                        ...airport,
                        isSelected: true,
                    },
                ]),
            );
        } else {
            dispatch(
                setDepartureFrom([
                    {
                        code: text,
                        isSelected: true,
                        name: '',
                    },
                ]),
            );
        }
    } catch (error) {
        console.error(error);
        dispatch(
            setDepartureFrom([
                {
                    code: text,
                    isSelected: true,
                    name: '',
                },
            ]),
        );
    }
};

// Lowercases and strips diacritics so 'malaga' matches 'Málaga'
const normalizeForSearch = (value) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const getDesireDestinationFullObject = async ({ text, locale, dispatch }) => {
    try {
        const destinationCode = extractDestinationCode(text);

        if (destinationCode === 'Anywhere') {
            dispatch(setGPTTo('Anywhere'));
            return dispatch(setDesireCountry({
                airportCode: "001",
                displayName: locale?.toLowerCase()?.includes('en') ?
                    "Surprise me - let Genius choose for you" :
                    "Verras me - laat Genius jouw bestemming kiezen"
                ,
                geoLocation: null,
                id: "00001",
                keyword: "Anywhere",
                type: "MULTICITY"
            }))
        }

        if (!destinationCode) {
            dispatch(setGPTTo(''));
            dispatch(setDesireCountry(null));
            return;
        }

        const searchTermLower = normalizeForSearch(destinationCode);

        const params = {
            searchTerm: destinationCode,
            Locale: locale,
        };

        const response = await SearchCountries(params);
        const locations = response?.locations || [];

        // Find the VERY FIRST match (case- and accent-insensitive)
        const location = locations.find(loc =>
            typeof loc?.keyword === 'string' &&
            normalizeForSearch(loc.keyword).includes(searchTermLower)
        );

        if (location) {
            dispatch(setDesireCountry(location));
            dispatch(setGPTTo(location.keyword.split(',')[0]));
        }
    } catch (error) {
        console.error(error);
    }
};

// export const getDesireDestinationFullObject = async ({text, locale, dispatch}) => {
//     try {
//         if (!extractDestinationCode(text)) {
//             dispatch(setGPTTo(''));
//             dispatch(setDesireCountry(null));
//             return;
//         }
//         const params = {
//             searchTerm: extractDestinationCode(text),
//             Locale: locale,
//         };
//         console.log({params}, ' for getting desire destination');
//         const response = await SearchCountries(params);
//         const locations = response?.locations;
//         console.log(JSON.stringify(locations), 'locations found');
//         const location = locations?.find(f => f?.keyword?.includes(extractDestinationCode(text)));
//         console.log({location}, 'for destination');
//         if (location) {
//             dispatch(setDesireCountry(location));
//             dispatch(setGPTTo(location?.keyword?.split(',')[0]));
//         }
//     } catch (error) {
//         console.log({error});
//     }
// };
//
export const extractDestinationCode = input => {
    // Pattern for `[BCN][6051594][L'Estartit, Torroella de Montgrí, Catalonia, Spain]`
    // Should return the third bracketed part if it’s not "Anywhere"
    const matchBracketPattern = input.match(/^\[.*?\]\[.*?\]\[(.*?)\]$/);
    if (matchBracketPattern && matchBracketPattern[1] !== 'Anywhere') {
        return matchBracketPattern[1];
    }

    // Pattern for `BCN` or any three-letter uppercase code (returns the string itself)
    if (/^[A-Z]{3}$/.test(input)) {
        return input;
    }

    // For `[ALL][000001][Anywhere]` or `Anywhere`, return null
    if (input === 'Anywhere' || /^\[ALL\]\[\d+\]\[Anywhere\]$/.test(input)) {
        return 'Anywhere';
    }

    // For any other string (multiple words or single words not excluded above), return it as is
    return input;
};

export function getBestMatch(searchTerm, options) {
    const termWords = searchTerm
        .toLowerCase()
        .split(/[\s,]+/)
        .filter(Boolean);

    let bestMatch = null;
    let maxScore = -1;

    for (const loc of options) {
        const keyword = loc.keyword.toLowerCase();
        let score = 0;
        for (const word of termWords) {
            if (keyword.includes(word)) {
                score++;
            }
        }

        if (score > maxScore) {
            maxScore = score;
            bestMatch = loc;
        }
    }

    // A score of 0 means no option shared a single word with the search term.
    // Returning the first option in that case presents a non-match as a match.
    return maxScore > 0 ? bestMatch : null;
}

export const getTravelersString = (T, adults, childs, infants, travelers) => {
    return `${adults} ${adults > 1 ? T('Common:Adults') : T('Common:Adult')}, ${childs} ${childs > 1 ? T('Common:Childs') : T('Common:Child')
        }, ${infants} ${infants > 1 ? T('Common:Infants') : T('Common:Infant')} - ${travelers} ${travelers > 1 ? T('Common:Rooms') : T('Common:Room')
        }`;
};

export const extractDestinationDisplay = (rawDestination) => {
    if (!rawDestination) {
        return '';
    } else if (rawDestination.includes('Anywhere')) {
        return 'Anywhere';
    } else {
        // Check for bracket format [CODE][ID][NAME]
        const bracketMatch = rawDestination.match(/^\[(.*?)\]\[.*?\]\[.*?\]$/);
        if (bracketMatch) {
            return bracketMatch[1] === '001' || bracketMatch[1] === '00001' ? 'Anywhere' : bracketMatch[1];
        } else {
            // Plain text format: split by spaces/commas
            const words = rawDestination.split(/[\s,]+/).filter(Boolean);
            if (words.length === 0) {
                return '';
            } else if (words.length === 1) {
                return words[0];
            } else {
                // Return first word if > 2 chars, else second word
                return words[0].length > 2 ? words[0] : words[1];
            }
        }
    }
};

export function formatFlightSummary(data, locale = 'en') {
    // Extract locale code (e.g., 'nl' from 'nl_NL')
    const momentLocale = locale ? locale.split('_')[0].toLowerCase() : 'en';

    const formattedDate = moment(new Date(data?.date)).locale(momentLocale).format('MMM DD, YYYY - h:mm A');
    const departure = data?.FlightSegmentList?.[0]?.Departure?.CodeList?.join(', ') || '';
    const rawDestination = data?.FlightSegmentList?.[0]?.Arrival?.CodeList?.[0] || '';

    const destination = extractDestinationDisplay(rawDestination);

    let adults = 0,
        children = 0,
        infants = 0;
    const roomSet = new Set();
    data?.PassengerAgeRoomItemList?.forEach(p => {
        roomSet.add(p.Room);
        const category = String(p?.PassengerCategory ?? '').toLowerCase();
        if (category === 'adult') {
            adults++;
        } else if (category === 'child') {
            children++;
        } else if (category === 'infant') {
            infants++;
        }
    });
    const travelers = adults + children + infants;
    const startDate = data?.FlightSegmentList?.[0]?.Departure?.Date;
    const endDate = data?.FlightSegmentList?.[1]?.Departure?.Date;
    const formattedStart = moment(startDate).locale(momentLocale).format('MMM DD');
    const formattedEnd = moment(endDate).locale(momentLocale).format('DD');
    const nights = moment(endDate).diff(moment(startDate), 'days');
    // const duration = `${formattedStart} - ${formattedEnd} (${nights} ${nights > 1 ? T('Common:Nights') : T('Common:Night')})`;
    const duration = `${formattedStart} - ${formattedEnd}`;
    return { id: data?.id, date: formattedDate, departure, destination, travelers, duration, nights };
}